import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  Button,
  Flex,
} from "@chakra-ui/react";
import { FaCheckCircle, FaBolt, FaDollarSign, FaClock, FaTools } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../../brandConfig";

const OffGridSolarPage = () => {
  const navigate = useNavigate();

  return (
    <>
      <NavbarWithCallToAction />

      <Box
        bg={getColor("background.main")}
        minHeight="100vh"
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(60% 60% at 0% 0%, #112334 0%, transparent 50%),
            radial-gradient(50% 50% at 50% 50%, #369eff33 0%, transparent 100%)
          `,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        {/* Hero Section */}
        <Box
          position="relative"
          width="100vw"
          height="500px"
          overflow="hidden"
          marginLeft="50%"
          transform="translateX(-50%)"
          backgroundImage="url('/assets/onesolarimages/abouthero.jpg')"
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundRepeat="no-repeat"
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(0, 0, 0, 0.5)"
            zIndex={1}
          />

          <Container
            maxW="1200px"
            position="relative"
            zIndex={2}
            height="100%"
            display="flex"
            alignItems="center"
          >
            <VStack spacing={6} align="flex-start" maxW="800px">
              <Heading
                as="h1"
                fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
                fontWeight="bold"
                color="white"
                textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                fontFamily="'Open Sans', sans-serif"
                textTransform="uppercase"
              >
                Off Grid Solar Systems
              </Heading>
              <Text
                fontSize={{ base: "xl", md: "2xl" }}
                color="white"
                textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                fontFamily="'Open Sans', sans-serif"
                fontWeight="600"
                textTransform="uppercase"
              >
                Independent Energy Living
              </Text>
              <Button
                size="lg"
                bg="white"
                color="#121c47"
                px={8}
                py={6}
                fontSize="md"
                fontWeight="600"
                textTransform="uppercase"
                borderRadius="4px"
                fontFamily="'Open Sans', sans-serif"
                _hover={{
                  bg: "gray.100",
                  transform: "translateY(-2px)",
                  boxShadow: "lg"
                }}
                transition="all 0.3s"
                onClick={() => navigate("/contact")}
              >
                Get Off Grid Quote
              </Button>
            </VStack>
          </Container>
        </Box>

        {/* Key Features Banner */}
        <Box
          bg="#121c47"
          py={{ base: 8, md: 12 }}
          position="relative"
          zIndex={2}
        >
          <Container maxW="1200px">
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaBolt size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  DC & AC Coupled Systems
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaDollarSign size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  $0 Upfront Cost
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaClock size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  Ready in 2 Weeks
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaTools size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  Post-Installation Service
                </Text>
              </Flex>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Main Content */}
        <Box
          bg="white"
          py={{ base: 16, md: 20 }}
          position="relative"
          zIndex={2}
        >
          <Container maxW="1200px">
            <VStack spacing={16} align="stretch">
              {/* Introduction */}
              <Box>
                <Box width="60px" height="2px" bg="#121c47" mb={4} />
                <Heading
                  as="h2"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                  textTransform="uppercase"
                >
                  Off-Grid Solar Solutions
                </Heading>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                  mb={4}
                >
                  One Solar New Zealand offers a diverse selection of off-grid energy systems, tailored to meet the needs of individuals seeking reliable off-the-grid solutions. Whether you need power for sheds, farms, country homes, or commercial operations, we have the expertise to deliver.
                </Text>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                >
                  Our approach is not "one size fits all." We understand that every property and energy requirement is unique, which is why we customize our solutions to match your specific needs and future goals.
                </Text>
              </Box>

              {/* Team Expertise */}
              <Box bg="#f8f9fa" p={{ base: 6, md: 8 }} borderRadius="lg">
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={4}
                >
                  Expert Off-Grid Solar Team
                </Heading>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="#666"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.8"
                >
                  Our in-house off-grid solar team consists of specialized experts who create standalone power systems for both residential and commercial properties. With years of experience in designing and installing off-grid solutions, we bring technical expertise and practical knowledge to every project.
                </Text>
              </Box>

              {/* Benefits */}
              <Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                >
                  Why Choose Off-Grid Solar?
                </Heading>
                <List spacing={4}>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Energy Independence
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Complete freedom from the grid with reliable 24/7 power supply
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Significant Cost Savings
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Eliminate ongoing electricity bills and avoid expensive grid connection costs
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Environmental Benefits
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Reduce carbon footprint and contribute to a greener planet
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Scalable Solutions
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Systems designed to grow with your energy needs over time
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Perfect for Remote Locations
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Ideal for properties where grid connection is impractical or prohibitively expensive
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                </List>
              </Box>

              {/* 3-Step Process */}
              <Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={8}
                  textAlign="center"
                  textTransform="uppercase"
                >
                  Our 3-Step Process
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                  <Box
                    bg="#f8f9fa"
                    p={6}
                    borderRadius="lg"
                    textAlign="center"
                    transition="all 0.3s"
                    _hover={{ transform: "translateY(-4px)", boxShadow: "lg" }}
                  >
                    <Text
                      fontSize="4xl"
                      fontWeight="bold"
                      color="#121c47"
                      mb={4}
                    >
                      1
                    </Text>
                    <Heading
                      as="h4"
                      fontSize="xl"
                      fontWeight="bold"
                      color="#050303"
                      fontFamily="'Open Sans', sans-serif"
                      mb={3}
                    >
                      ASSESS
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif">
                      We evaluate your property, analyze your current energy usage, and understand your specific requirements to design the perfect off-grid system.
                    </Text>
                  </Box>

                  <Box
                    bg="#f8f9fa"
                    p={6}
                    borderRadius="lg"
                    textAlign="center"
                    transition="all 0.3s"
                    _hover={{ transform: "translateY(-4px)", boxShadow: "lg" }}
                  >
                    <Text
                      fontSize="4xl"
                      fontWeight="bold"
                      color="#121c47"
                      mb={4}
                    >
                      2
                    </Text>
                    <Heading
                      as="h4"
                      fontSize="xl"
                      fontWeight="bold"
                      color="#050303"
                      fontFamily="'Open Sans', sans-serif"
                      mb={3}
                    >
                      EDUCATE
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif">
                      We explain your options, provide detailed product information, discuss pricing, and help you secure available rebates and payment plans.
                    </Text>
                  </Box>

                  <Box
                    bg="#f8f9fa"
                    p={6}
                    borderRadius="lg"
                    textAlign="center"
                    transition="all 0.3s"
                    _hover={{ transform: "translateY(-4px)", boxShadow: "lg" }}
                  >
                    <Text
                      fontSize="4xl"
                      fontWeight="bold"
                      color="#121c47"
                      mb={4}
                    >
                      3
                    </Text>
                    <Heading
                      as="h4"
                      fontSize="xl"
                      fontWeight="bold"
                      color="#050303"
                      fontFamily="'Open Sans', sans-serif"
                      mb={3}
                    >
                      INSTALL
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif">
                      Our qualified technicians install your system efficiently, ensuring everything is tested and ready to deliver reliable, independent power.
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>

              {/* Applications */}
              <Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                >
                  Perfect For
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <Box
                    border="2px solid"
                    borderColor="#121c47"
                    p={6}
                    borderRadius="lg"
                  >
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={3}>
                      Sheds & Workshops
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif">
                      Power tools, lighting, and equipment without expensive trenching or grid connections
                    </Text>
                  </Box>
                  <Box
                    border="2px solid"
                    borderColor="#121c47"
                    p={6}
                    borderRadius="lg"
                  >
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={3}>
                      Farms & Rural Properties
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif">
                      Reliable power for irrigation, livestock, and remote farm buildings
                    </Text>
                  </Box>
                  <Box
                    border="2px solid"
                    borderColor="#121c47"
                    p={6}
                    borderRadius="lg"
                  >
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={3}>
                      Country Homes
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif">
                      Complete household power independence for remote or rural residential properties
                    </Text>
                  </Box>
                  <Box
                    border="2px solid"
                    borderColor="#121c47"
                    p={6}
                    borderRadius="lg"
                  >
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={3}>
                      Commercial Operations
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif">
                      Large-scale off-grid solutions for businesses and industrial applications
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>

              {/* CTA Section */}
              <Box
                bg="#121c47"
                color="white"
                p={{ base: 8, md: 12 }}
                borderRadius="lg"
                textAlign="center"
              >
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
                  fontWeight="bold"
                  fontFamily="'Open Sans', sans-serif"
                  mb={4}
                >
                  Ready to Go Off-Grid?
                </Heading>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                  maxW="700px"
                  mx="auto"
                >
                  Contact us today for a free consultation and custom quote. Our experts will design the perfect off-grid solar solution for your property.
                </Text>
                <Button
                  size="lg"
                  bg="white"
                  color="#121c47"
                  px={8}
                  py={6}
                  fontSize="md"
                  fontWeight="600"
                  textTransform="uppercase"
                  borderRadius="4px"
                  fontFamily="'Open Sans', sans-serif"
                  _hover={{
                    bg: "gray.100",
                    transform: "translateY(-2px)",
                    boxShadow: "lg"
                  }}
                  transition="all 0.3s"
                  onClick={() => navigate("/contact")}
                >
                  Get Your Free Quote
                </Button>
              </Box>
            </VStack>
          </Container>
        </Box>

        <FooterWithFourColumns />
      </Box>
    </>
  );
};

export default OffGridSolarPage;
